import moment from 'moment-timezone';
import { DATE_FORMAT, REGEX, STRINGS } from '../../../constants';
import {
  addCalendarDaysToIsoDate,
  clampIsoDateToRange,
  getUkPrebookTimeReadyDefaultsOneHourFromNow,
} from '../../../utils/dateAndTime';
import { flattenCitysprintParcelServiceCards } from '../../../helpers/mapCitysprintQuoteToParcelSections';
import { Validators } from '../../../helpers';
import { StringUtil } from '../../../utils';
import {
  CITYSPRINT_INSTRUCTIONS_MAX_LENGTH,
  REVIEW_BOOKING_DATETIME_FORMAT,
} from '../constants';

const { asString, trimPart } = StringUtil;

function pickPostcode(kind, booking, form) {
  const isDelivery = kind === 'delivery';
  const override = isDelivery
    ? form?.deliveryPostcodeField
    : form?.collectionPostcodeField;
  const fallback = isDelivery
    ? booking?.deliveryPostcode
    : booking?.collectionPostcode;
  const src = isDelivery ? booking?.delivery : booking?.collection;
  return override || src?.postcode || fallback || '';
}

function pickCompany(kind, form, src) {
  const override =
    kind === 'delivery' ? form?.deliveryCompany : form?.collectionCompany;
  return src?.organisation || override || '';
}

export function buildCitysprintAddressForWidget(kind, booking, form) {
  const src =
    kind === 'delivery' ? booking?.delivery || {} : booking?.collection || {};
  return {
    organisation: pickCompany(kind, form, src),
    street: src.street || '',
    locality: src.locality || '',
    town: src.town || '',
    county: src.county || '',
    postcode: pickPostcode(kind, booking, form),
    _coordinates: src._coordinates,
  };
}

export function isCitysprintUkPostcodeValid(postcode) {
  const value = String(postcode ?? '').trim();
  if (!value) {
    return false;
  }
  return Validators.isUKPostcodeValid(value);
}

export function getCitysprintUkPostcodeError(postcode) {
  const value = String(postcode ?? '').trim();
  if (!value) {
    return null;
  }
  if (isCitysprintUkPostcodeValid(value)) {
    return null;
  }
  return `${STRINGS.INVALID_POSTCODE}. ${StringUtil.formatMessage(
    STRINGS.PLEASE_ENTER_VALID_$,
    'UK postcode'
  )}.`;
}

export function hasCitysprintAddressCoreFields(address) {
  if (!address || typeof address !== 'object') {
    return false;
  }
  const postcode = String(address.postcode ?? '').trim();
  const street = String(address.street ?? '').trim();
  const town = String(address.town ?? '').trim();
  return Boolean(
    postcode && street && town && isCitysprintUkPostcodeValid(postcode)
  );
}

export function findMatchingQuoteCard(quoteLayout, serviceTierId, vehicleKey) {
  if (!serviceTierId || !vehicleKey || !quoteLayout) {
    return null;
  }
  for (const card of flattenCitysprintParcelServiceCards(quoteLayout)) {
    if (
      card.section?.id === serviceTierId &&
      card.vehicle?.key === vehicleKey
    ) {
      return card;
    }
  }
  return null;
}

export function formatCollectionDateDisplay(iso, todayIso) {
  const d = String(iso ?? '').trim();
  if (!d) {
    return '';
  }
  if (d === todayIso) {
    return 'Today';
  }
  const parsed = moment.tz(
    d,
    DATE_FORMAT.YYYY_MM_DD_FORMAT,
    DATE_FORMAT.EUROPE_LONDON_TIME_ZONE
  );
  return parsed.isValid() ? parsed.format('D MMMM YYYY') : d;
}

function shallowCloneForm(form) {
  if (!form || typeof form !== 'object') {
    return form;
  }
  return { ...form };
}

/* eslint-disable complexity -- default field merge from route state */
export function applyCitysprintCollectionContactPrefill(form, prefill) {
  if (!form || !prefill || typeof prefill !== 'object') {
    return form;
  }

  const next = { ...form };
  const contactName = asString(prefill.contactName);
  const email = asString(prefill.email);
  const telephone = asString(prefill.telephone);

  if (!trimPart(next.collectionFullName) && contactName) {
    next.collectionFullName = contactName;
  }
  if (!trimPart(next.collectionEmail) && email) {
    next.collectionEmail = email;
  }
  if (!trimPart(next.collectionPhone) && telephone) {
    next.collectionPhone = telephone;
  }

  return next;
}

export function buildInitialForm(booking, locationState, todayIso) {
  const s = locationState || {};
  const fromFirstLast = [s.firstName, s.surname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const yourFromState = asString(s.yourFullName);
  return {
    yourFullName: yourFromState || fromFirstLast,
    firstName: asString(s.firstName),
    surname: asString(s.surname),
    email: asString(s.email),
    mobile: asString(s.mobile),
    itemCount: s.itemCount ?? '1',
    collectionFullName:
      (typeof s.collectionFullName === 'string' && s.collectionFullName) ||
      (typeof s.collectionContactName === 'string' &&
        s.collectionContactName) ||
      '',
    collectionEmail: asString(s.collectionEmail),
    collectionPhone: asString(s.collectionPhone),
    collectionInstructions: asString(s.collectionInstructions),
    collectionCompany: asString(s.collectionCompany),
    collectionPostcodeField:
      s.collectionPostcodeField ?? booking.collectionPostcode,
    deliveryFullName:
      (typeof s.deliveryFullName === 'string' && s.deliveryFullName) ||
      (typeof s.deliveryContactName === 'string' && s.deliveryContactName) ||
      '',
    deliveryEmail: asString(s.deliveryEmail),
    deliveryPhone: asString(s.deliveryPhone),
    deliveryInstructions: asString(s.deliveryInstructions),
    deliveryCompany: asString(s.deliveryCompany),
    deliveryPostcodeField: s.deliveryPostcodeField ?? booking.deliveryPostcode,
    collectionDate: (() => {
      const raw = s.collectionDate;
      if (raw == null || String(raw).trim() === '') {
        return '';
      }
      const trimmed = String(raw).trim();
      const maxIso = addCalendarDaysToIsoDate(todayIso, 7);
      return clampIsoDateToRange(trimmed, todayIso, maxIso);
    })(),
    ...(() => {
      const h = s.timeReadyHour ?? booking.timeHour;
      const mi = s.timeReadyMinute ?? booking.timeMinute;
      const hasH = h != null && String(h).trim() !== '';
      const hasM = mi != null && String(mi).trim() !== '';
      if (hasH && hasM) {
        return {
          timeReadyHour: String(h).padStart(2, '0'),
          timeReadyMinute: String(mi).padStart(2, '0'),
        };
      }
      const fb = getUkPrebookTimeReadyDefaultsOneHourFromNow();
      return { timeReadyHour: fb.hour, timeReadyMinute: fb.minute };
    })(),
  };
}

function pickMergedCollectionDate(cleanedForm, booking) {
  if (
    cleanedForm != null &&
    Object.prototype.hasOwnProperty.call(cleanedForm, 'collectionDate')
  ) {
    const v = cleanedForm.collectionDate;
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return booking.collectionDate ?? '';
}

function mergeBookingAndForm(booking, form) {
  const cleanedForm = shallowCloneForm(form);
  const parcelTimeHour = booking.timeHour ?? cleanedForm.timeReadyHour;
  const parcelTimeMinute = booking.timeMinute ?? cleanedForm.timeReadyMinute;

  const latestDeliveryTime = [
    booking.latestDeliveryTime,
    cleanedForm.latestDeliveryTime,
  ].find(t => typeof t === 'string' && String(t).trim());

  return {
    ...booking,
    ...cleanedForm,
    timeHour: parcelTimeHour,
    timeMinute: parcelTimeMinute,
    collectionDate: pickMergedCollectionDate(cleanedForm, booking),
    deliveryContactName:
      cleanedForm.deliveryFullName ??
      cleanedForm.deliveryContactName ??
      booking.deliveryContactName,
    collectionContactName:
      cleanedForm.collectionFullName ??
      cleanedForm.collectionContactName ??
      booking.collectionContactName,
    ...(latestDeliveryTime?.trim()
      ? { latestDeliveryTime: latestDeliveryTime.trim() }
      : {}),
  };
}

export function formatReviewDateTimeFromIso(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const m = moment(value).tz(DATE_FORMAT.EUROPE_LONDON_TIME_ZONE);
    return m.isValid() ? m.format(REVIEW_BOOKING_DATETIME_FORMAT) : null;
  }
  const s = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!s) {
    return null;
  }
  const parsed = moment(s);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed
    .clone()
    .tz(DATE_FORMAT.EUROPE_LONDON_TIME_ZONE)
    .format(REVIEW_BOOKING_DATETIME_FORMAT);
}

function formatPrebookCollectionReviewLine(data) {
  if (data?.bookingMode !== 'prebook') {
    return null;
  }
  const d = String(data.collectionDate ?? '').trim();
  const hhRaw = data.timeHour ?? data.timeReadyHour;
  const mmRaw = data.timeMinute ?? data.timeReadyMinute;
  const hh = hhRaw != null ? String(hhRaw).padStart(2, '0') : '';
  const mm = mmRaw != null ? String(mmRaw).padStart(2, '0') : '';
  if (!d || !hh || !mm) {
    return null;
  }
  const parsed = moment.tz(
    `${d} ${hh}:${mm}:00`,
    `${DATE_FORMAT.YYYY_MM_DD_FORMAT} HH:mm:ss`,
    DATE_FORMAT.EUROPE_LONDON_TIME_ZONE
  );
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format(REVIEW_BOOKING_DATETIME_FORMAT);
}

export function formatReviewCollectionDateTime(
  data,
  collectionScheduleFallback
) {
  const prebook = formatPrebookCollectionReviewLine(data);
  if (prebook) {
    return prebook;
  }
  const fromQuote = formatReviewDateTimeFromIso(data?.latestCollectionTime);
  if (fromQuote) {
    return fromQuote;
  }
  const fb =
    typeof collectionScheduleFallback === 'string'
      ? collectionScheduleFallback.trim()
      : '';
  return fb || '—';
}

export function buildCitysprintThreeDSecureData(data) {
  if (!data || typeof data !== 'object') {
    return {
      email: '',
      mobilePhoneNumber: '',
      billingAddress: {
        givenName: '',
        surname: '',
        phoneNumber: '',
        streetAddress: '',
        extendedAddress: '',
        locality: '',
        region: '',
        postalCode: '',
        countryCodeAlpha2: 'GB',
      },
    };
  }

  const collection = data.collection || {};
  const postcode = data.collectionPostcodeField || collection.postcode || '';
  const contactName = (
    data.collectionFullName ||
    data.collectionContactName ||
    data.yourFullName ||
    ''
  ).trim();
  const parts = contactName.split(/\s+/).filter(Boolean);
  const givenName = parts[0] || '';
  const surname = parts.length > 1 ? parts.slice(1).join(' ') : givenName;
  const phone = data.collectionPhone || data.mobile || '';

  return {
    email: data.email || '',
    mobilePhoneNumber: phone,
    billingAddress: {
      givenName,
      surname,
      phoneNumber: phone,
      streetAddress: collection.street || '',
      extendedAddress: [collection.organisation, collection.locality]
        .filter(Boolean)
        .join(', '),
      locality: collection.town || '',
      region: collection.county || '',
      postalCode: postcode,
      countryCodeAlpha2: collection.countryCode || 'GB',
    },
  };
}

export function buildCitysprintCheckoutPayload(data, paymentNonce) {
  const collection = data?.collection || {};
  const delivery = data?.delivery || {};

  const collectionPhone = trimPart(data?.collectionPhone);
  const collectionEmail = trimPart(data?.collectionEmail);
  const collectionInstructions = trimPart(data?.collectionInstructions);
  const collectionOrg = trimPart(
    collection.organisation ?? data?.collectionCompany
  );
  const deliveryPhone = trimPart(data?.deliveryPhone);
  const deliveryEmail = trimPart(data?.deliveryEmail);
  const deliveryInstructions = trimPart(
    data?.deliveryInstructions ?? delivery.deliveryInstructions
  );
  const deliveryOrg = trimPart(delivery.organisation ?? data?.deliveryCompany);

  return {
    paymentNonce,
    collection: {
      contact: {
        name: trimPart(data?.collectionFullName ?? data?.collectionContactName),
        phoneNumber: collectionPhone,
        ...(collectionEmail && { email: collectionEmail }),
      },
      address: {
        ...(collectionOrg && { organisation: collectionOrg }),
        addressLine1: trimPart(collection.street),
        addressLine2: trimPart(collection.locality),
        town: trimPart(collection.town),
        county: trimPart(collection.county),
        postcode: trimPart(
          data?.collectionPostcodeField ??
            collection.postcode ??
            data?.collectionPostcode
        ),
      },
      ...(collectionInstructions && {
        instructions: collectionInstructions,
      }),
    },
    delivery: {
      contact: {
        name: trimPart(data?.deliveryFullName ?? data?.deliveryContactName),
        ...(deliveryPhone && { phoneNumber: deliveryPhone }),
        ...(deliveryEmail && { email: deliveryEmail }),
      },
      address: {
        ...(deliveryOrg && { organisation: deliveryOrg }),
        addressLine1: trimPart(delivery.street),
        addressLine2: trimPart(delivery.locality),
        town: trimPart(delivery.town),
        county: trimPart(delivery.county),
        postcode: trimPart(
          data?.deliveryPostcodeField ??
            delivery.postcode ??
            data?.deliveryPostcode
        ),
      },
      ...(deliveryInstructions && {
        instructions: deliveryInstructions,
      }),
    },
  };
}

export function buildBookingFromParcelStep(parcelStep) {
  const ps = parcelStep || {};
  const q = ps.selectedQuote || {};
  return {
    collectionPostcode: ps.collection?.postcode ?? '',
    deliveryPostcode: ps.delivery?.postcode ?? '',
    bookingMode: 'prebook',
    collectionDate: ps.collectionDate ?? '',
    timeHour: ps.timeHour ?? '',
    timeMinute: ps.timeMinute ?? '',
    serviceTierId: q.section?.id ?? '',
    serviceTierTitle: q.section?.title ?? '',
    serviceName: q.serviceName ?? q.section?.title ?? '',
    vehicleKey: q.vehicle?.key ?? '',
    vehicleName: q.vehicle?.name ?? '',
    vehicle: q.vehicle ?? null,
    priceExcl: q.prices?.excl ?? '',
    priceIncl: q.prices?.incl ?? '',
    delivery: ps.delivery,
    collection: ps.collection,
    ...(typeof q.latestDeliveryTime === 'string' && q.latestDeliveryTime.trim()
      ? { latestDeliveryTime: q.latestDeliveryTime.trim() }
      : {}),
    ...(typeof q.selectionKey === 'string' && q.selectionKey
      ? { quoteSelectionKey: q.selectionKey }
      : {}),
  };
}

export function buildMergedDataFromSlice(parcelStep, detailsStep) {
  return mergeBookingAndForm(
    buildBookingFromParcelStep(parcelStep),
    detailsStep || {}
  );
}

export function formatCitysprintSummaryVehicleCaption(vehicle) {
  if (!vehicle || typeof vehicle !== 'object') {
    return '';
  }
  const dims = String(vehicle.dimensions ?? '')
    .replace(/×/g, 'x')
    .trim();
  const maxKg = vehicle.maxKg;
  const hasMaxKg = maxKg != null && maxKg !== '' && maxKg !== '—';
  const maxPart = hasMaxKg ? ` - Max ${maxKg}kg` : '';
  if (!dims || dims === '—') {
    return maxPart ? maxPart.replace(/^\s*-\s*/, '') : '';
  }
  return `${dims}${maxPart}`;
}

export function getCitysprintSummaryServiceLabel(source) {
  if (!source || typeof source !== 'object') {
    return '';
  }
  return (
    source.serviceName ?? source.section?.title ?? source.serviceTierTitle ?? ''
  );
}

export function buildCitysprintSummaryBasketItem(booking, form) {
  const ex = parseFloat(booking.priceExcl);
  const inc = parseFloat(booking.priceIncl);
  const qty = Math.max(1, Number(form?.itemCount) || 1);
  const contactName =
    form?.deliveryFullName?.trim() || form?.deliveryContactName?.trim() || '';
  return {
    basketItemId: 'citysprint-details',
    price: {
      basePrice: Number.isFinite(ex) ? ex : 0,
      totalIncVatAmount: Number.isFinite(inc) ? inc : 0,
    },
    shipment: {
      outboundConsignment: {
        deliveryDetails: {
          contactDetails: {
            contactName,
          },
        },
        parcels: [
          {
            _parcelId: 'citysprint-parcel',
            _quantity: qty,
            _weight: 1,
            _height: 30,
            _width: 30,
            _length: 30,
            _value: 50,
          },
        ],
        _network: {
          networkDesc: [booking.serviceTierTitle, booking.vehicleName]
            .filter(Boolean)
            .join(' · '),
          insurance: false,
        },
      },
    },
  };
}

const NAME_MAX_LENGTH = 35;
const EMAIL_MAX_LENGTH = 100;
const PHONE_MAX_LENGTH = 15;

function addNameError(nameKey, form, errors, required) {
  const name = String(form?.[nameKey] ?? '').trim();
  if (!name) {
    if (required) {
      errors[nameKey] = STRINGS.PLEASE_ENTER_YOUR_CONTACT_NAME;
    }
    return;
  }
  if (name.length > NAME_MAX_LENGTH) {
    errors[nameKey] = STRINGS.PLEASE_ENTER_YOUR_CONTACT_NAME;
  }
}

function addEmailError(emailKey, form, errors, required) {
  const email = String(form?.[emailKey] ?? '').trim();
  if (!email) {
    if (required) {
      errors[emailKey] = STRINGS.PLEASE_ENTER_YOUR_EMAIL;
    }
    return;
  }
  if (email.length > EMAIL_MAX_LENGTH || !REGEX.EMAIL_REGEX.test(email)) {
    errors[emailKey] = STRINGS.PLEASE_ENTER_YOUR_VALID_EMAIL;
  }
}

function addPhoneError(phoneKey, form, errors, required) {
  const phone = String(form?.[phoneKey] ?? '')
    .trim()
    .replace(/\s/g, '');
  if (!phone) {
    if (required) {
      errors[phoneKey] = STRINGS.PLEASE_ENTER_YOUR_PHONE;
    }
    return;
  }
  if (
    phone.length > PHONE_MAX_LENGTH ||
    !REGEX.COMMON_PHONE_NUMBER_REGEXP.test(phone)
  ) {
    errors[phoneKey] = STRINGS.PLEASE_ENTER_VALID_PHONE;
  }
}

function addInstructionsError(instructionsKey, form, errors) {
  const instructions = String(form?.[instructionsKey] ?? '');
  if (!instructions) {
    return;
  }
  if (instructions.length > CITYSPRINT_INSTRUCTIONS_MAX_LENGTH) {
    errors[
      instructionsKey
    ] = `Instructions must be ${CITYSPRINT_INSTRUCTIONS_MAX_LENGTH} characters or fewer`;
  }
}

export function getCitysprintContactTripletErrors(form, keys) {
  const {
    nameKey,
    emailKey,
    phoneKey,
    instructionsKey,
    nameRequired = false,
    emailRequired = false,
    phoneRequired = false,
  } = keys;
  const errors = {};
  addNameError(nameKey, form, errors, nameRequired);
  addEmailError(emailKey, form, errors, emailRequired);
  addPhoneError(phoneKey, form, errors, phoneRequired);
  if (instructionsKey) {
    addInstructionsError(instructionsKey, form, errors);
  }
  return errors;
}
